/**
 * useRecordProcessAutostart — wskaźnik „proces się uruchamia…" po create (F17 follow-up).
 *
 * Problem: przy zapisie rekordu, dla którego automatyzacja modułu odpala `start_approval`,
 * start procesu jest fire-and-forget (`void startWorkflow(...)` w silniku automatyzacji) —
 * strona detalu montuje się ZANIM instancja wyląduje w bazie, a nic jej nie odświeża. Backend
 * (`willAutoStartApproval`) zwraca w odpowiedzi create zgrubny hint `processAutoStarting`;
 * front uzbraja na jego podstawie krótki, ograniczony poll na pojawienie się instancji.
 *
 * KRYTYCZNA pułapka (potwierdzona przeglądem planu wobec routera): `/contractors/new` →
 * `/contractors/:id` renderują TEN SAM `<ContractorDetail/>` (bez `key`), analogicznie
 * `/modules/:slug/records/new` → `/modules/:slug/:recordId` renderują TEN SAM
 * `<RecordFormPage/>` (bez `key`) — patrz `TenantTabPanels.tsx`. Nawigacja create→detail
 * NIE remontuje strony, więc uzbrojenie przez `useState(() => location.state?...)`
 * (inicjalizator mount-time) NIGDY by się nie odpalił ponownie. Dlatego uzbrajamy
 * REAKTYWNIE — w `useEffect` zależnym od `location.state` — i od razu czyścimy state
 * historii, żeby powrót (wstecz) do tego samego wpisu historii nie uzbroił ponownie.
 *
 * Poll dzieli klucz query (`recordInstancesKey`) z bannerem/historią procesu — udany
 * refetch aktualizuje WSZYSTKICH obserwatorów tego klucza, bez ręcznej inwalidacji.
 *
 * Disarm (którykolwiek pierwszy):
 *  - pojawiła się żywa instancja (`isLiveInstanceStatus`),
 *  - `MAX_ATTEMPTS` rozliczeń zapytania (sukces LUB błąd — persystentnie failujące
 *    zapytanie też musi się kiedyś wyłączyć, inaczej pollowałoby/wskaźnik wisiałby wiecznie),
 *  - twardy limit czasu `MAX_ARMED_DURATION_MS` (~18s) od uzbrojenia.
 * Po timeout: cicho gasimy wskaźnik — brak error UI, spada do normalnego stanu wolnego slotu.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  isLiveInstanceStatus,
  recordInstancesKey,
  selectRecordInstanceRows,
} from '@/hooks/useActiveInstanceForRecord';
import { fetchInstancesForRecord } from '@/lib/processes/instance-api';

/** Kadencja pollu. */
const POLL_INTERVAL_MS = 1500;
/** Twardy limit rozliczeń (sukces+błąd) — ~18s przy kadencji 1.5s. */
const MAX_ATTEMPTS = 12;
/** Twardy limit czasu od uzbrojenia — niezależny od kadencji pollu. */
const MAX_ARMED_DURATION_MS = 18_000;

interface RecordLocationState {
  /** Ustawiane przez `navigate(path, { state: { processAutoStarting: true } })` po create. */
  processAutoStarting?: boolean;
}

export interface UseRecordProcessAutostartResult {
  /** `true` gdy uzbrojone i jeszcze bez żywej instancji — pokaż wskaźnik „uruchamia się…". */
  starting: boolean;
}

export function useRecordProcessAutostart(
  moduleCode: string,
  recordId: string,
): UseRecordProcessAutostartResult {
  const location = useLocation();
  const [armed, setArmed] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const lastSettledAtRef = useRef(0);

  // Uzbrojenie REAKTYWNE (patrz nagłówek pliku) — NIE mount-time initializer.
  useEffect(() => {
    const state = location.state as RecordLocationState | null;
    if (!state?.processAutoStarting) return;
    lastSettledAtRef.current = 0;
    setAttempts(0);
    setArmed(true);
    // Czyścimy TYLKO nasz `processAutoStarting` z wpisu historii (żeby reload/wstecz go nie
    // uzbroił ponownie), zachowując bookkeeping react-routera. `window.history.state` to
    // `{ usr, key, idx }` — `usr` jest nasze (== `location.state`), `key`/`idx` są routera
    // (scroll-restoration + delta wstecz/dalej). Nadpisanie całości `{}` skasowałoby key/idx
    // (patrz wzorzec w `lib/dev-tenant-id.ts` — spread zamiast nadpisania).
    window.history.replaceState({ ...window.history.state, usr: undefined }, '');
  }, [location.state]);

  // Twardy limit czasu od momentu uzbrojenia — niezależny od kadencji/liczby prób pollu.
  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), MAX_ARMED_DURATION_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  const query = useQuery({
    queryKey: recordInstancesKey(moduleCode, recordId),
    queryFn: () => fetchInstancesForRecord(moduleCode, recordId),
    enabled: !!moduleCode && !!recordId,
    select: selectRecordInstanceRows,
    // Funkcyjna forma czyta świeży stan zapytania w momencie decyzji o kolejnym
    // refetchu (unika lagu o jedną klatkę renderu vs. wartość policzona z zewnątrz).
    // `q.state.data` to surowa odpowiedź (przed `select`) — wiersze są w `rows`.
    refetchInterval: (q) => {
      if (!armed) return false;
      const data = q.state.data?.rows ?? [];
      const hasLive = data.some((row) => isLiveInstanceStatus(row.status));
      return hasLive ? false : POLL_INTERVAL_MS;
    },
  });

  const list = query.data ?? [];
  const hasLiveInstance = list.some((row) => isLiveInstanceStatus(row.status));

  // Licznik rozliczeń — sukces LUB błąd, każdy się liczy (react-query v5 nie ma już
  // onSuccess/onError na useQuery; `dataUpdatedAt`/`errorUpdatedAt` to jedyny sygnał
  // "zapytanie się rozliczyło" dostępny na poziomie hooka).
  useEffect(() => {
    if (!armed) return;
    const settledAt = Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
    if (settledAt === 0 || settledAt === lastSettledAtRef.current) return;
    lastSettledAtRef.current = settledAt;
    setAttempts((n) => n + 1);
  }, [armed, query.dataUpdatedAt, query.errorUpdatedAt]);

  // Disarm — żywa instancja LUB limit prób LUB (osobno, wyżej) limit czasu.
  useEffect(() => {
    if (!armed) return;
    if (hasLiveInstance || attempts >= MAX_ATTEMPTS) setArmed(false);
  }, [armed, hasLiveInstance, attempts]);

  return { starting: armed && !hasLiveInstance };
}
